import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, Twitter, Mail } from "lucide-react";
import { IMAGES, ORG } from "@/config/content";
import { useDonate } from "@/context/DonateContext";

export default function Footer() {
  const { openDonate } = useDonate();
  return (
    <footer className="relative border-t border-gold/20 bg-ink2" data-testid="footer">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 grid gap-12 md:grid-cols-[1.4fr_1fr_1fr]">
        <div>
          <div className="flex items-center gap-3">
            <img src={IMAGES.logo} alt="EHSIMFFA seal" className="h-14 w-14" />
            <div>
              <div className="font-serif text-2xl text-ivory">EHSIMFFA</div>
              <div className="text-xs uppercase tracking-[0.2em] text-gold/80">Friends in America</div>
            </div>
          </div>
          <p className="mt-6 font-serif text-xl text-ivory leading-snug">{ORG.headingName}</p>
          <p className="mt-1 text-base text-gold/90 leading-snug" lang="am">{ORG.amharicName}</p>
          <p className="mt-5 max-w-md text-ivory2/80 leading-relaxed">
            {ORG.mission}
          </p>
          <a href={`mailto:${ORG.email}`} data-testid="footer-contact-email" className="mt-6 inline-flex items-center gap-2 text-gold hover:text-goldhi">
            <Mail className="h-4 w-4" /> {ORG.email}
          </a>
        </div>

        <div>
          <h4 className="kicker mb-5">Explore</h4>
          <ul className="space-y-3 text-ivory2">
            <li><Link to="/about" className="hover:text-gold">About the Foundation</Link></li>
            <li><Link to="/legacy" className="hover:text-gold">His Legacy</Link></li>
            <li><Link to="/ethiopia" className="hover:text-gold">Our Work in Ethiopia</Link></li>
            <li><Link to="/board" className="hover:text-gold">Board Members</Link></li>
            <li><Link to="/gala" className="hover:text-gold">The Annual Gala</Link></li>
            <li><Link to="/contact" className="hover:text-gold">Contact</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="kicker mb-5">Support</h4>
          <button
            onClick={() => openDonate()}
            data-testid="footer-donate-btn"
            className="rounded-sm bg-gold px-6 py-3 text-sm font-bold uppercase tracking-wider text-ink hover:bg-goldhi"
          >
            Donate Now
          </button>
          <div className="mt-6 flex gap-3">
            {[
              { Icon: Facebook, href: ORG.social.facebook, label: "Facebook" },
              { Icon: Instagram, href: ORG.social.instagram, label: "Instagram" },
              { Icon: Linkedin, href: ORG.social.linkedin, label: "LinkedIn" },
              { Icon: Twitter, href: ORG.social.twitter, label: "Twitter" },
            ].map(({ Icon, href, label }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={label}
                className="flex h-10 w-10 items-center justify-center rounded-sm hairline text-ivory2 hover:text-gold hover:border-gold/50 transition-colors"
              >
                <Icon className="h-5 w-5" />
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-gold/10">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between text-xs text-ivory2/60">
          <p>© {new Date().getFullYear()} EHSIMFFA. A 501(c)(3) not-for-profit registered in the {ORG.state}. Donations are fully tax-deductible.</p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <a href="https://webnehtech.com" target="_blank" rel="noopener" className="hover:text-gold" title="Website design and online ticketing by WebnehTech Solutions">
              Website &amp; online ticketing by WebnehTech Solutions
            </a>
            <span className="text-gold/30" aria-hidden="true">·</span>
            <Link to="/admin/login" data-testid="footer-admin-link" className="hover:text-gold">Admin Portal</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
